import React from "react";
import { createSwitch, SwitchFrame, type SwitchProps, SwitchStyledContext, SwitchThumb, styled, Theme, withStaticProperties } from "tamagui";
import { Check as CheckIcon } from "@tamagui/lucide-icons";
import { Icon } from "../Icon";
const Frame = styled(SwitchFrame, {
  overflow: "hidden",
  padding: "$sp0.25",
  cursor: "pointer",
  variants: {
    unstyled: {
      false: {
        backgroundColor: "$textSecondary",
        focusVisibleStyle: {
          outlineColor: "$textPrimary",
          outlineStyle: "solid",
          outlineWidth: "$si0.25",
          outlineOffset: "$si0.25"
        }
      }
    },
    size: {
      "...size": () => ({
        width: "$si6",
        height: "$si4",
        minHeight: "$si4"
      })
    },
    disabled: {
      true: {
        backgroundColor: "$uiIconFill",
        opacity: 1,
        cursor: "not-allowed"
      }
    }
  } as const
});
const Thumb = styled(SwitchThumb, {
  alignItems: "center",
  justifyContent: "center",
  variants: {
    unstyled: {
      false: {
        backgroundColor: "$surfaceWhite"
      }
    },
    size: {
      "...size": () => ({
        width: 28,
        // $si3.5
        height: 28 // $si3.5
      })
    }
  } as const
});
const InternalSwitch = createSwitch({
  Frame,
  Thumb
});
const ThumbIcon: React.FC = () => {
  const {
    active,
    disabled
  } = SwitchStyledContext.useStyledContext();
  if (!active) {
    return null;
  }
  return <Icon icon={CheckIcon} size="$iconSmall" color={disabled ? "$uiIconFill" : "$textPrimary"} strokeWidth={2.5} data-sentry-element="Icon" data-sentry-component="ThumbIcon" data-sentry-source-file="Switch.tsx" />;
};
ThumbIcon.displayName = "SwitchThumbIcon";

/**
 * Bakes in the bouncy thumb-slide transition so consumers don't have to.
 * `width` is forwarded as a runtime prop so createSwitch can compute the
 * correct initial thumb offset without waiting for a layout pass.
 */
const AnimatedThumb = React.forwardRef<React.ComponentRef<typeof InternalSwitch.Thumb>, React.ComponentPropsWithRef<typeof InternalSwitch.Thumb>>((props, ref) => <InternalSwitch.Thumb transition="bouncyTight" width="$si4" {...props} ref={ref} />);
AnimatedThumb.displayName = "SwitchThumb";
const SwitchImpl = React.forwardRef<React.ComponentRef<typeof InternalSwitch>, React.ComponentPropsWithRef<typeof InternalSwitch>>((props, ref) => <Theme name="v2">
    <InternalSwitch transition="quick" animateOnly={["backgroundColor"]} activeStyle={{
    backgroundColor: props.disabled ? "$uiIconFill" : "$textPrimary"
  }} {...props} ref={ref} />
  </Theme>);
SwitchImpl.displayName = "Switch";
export const Switch = withStaticProperties(SwitchImpl, {
  Thumb: AnimatedThumb,
  ThumbIcon
});
export type { SwitchProps };